import { Employee } from "../../types/processTypes";

const POLL_MS = 100;
const TIMEOUT_MS = 8000;

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function employeePhotoUrls(employee: Employee): string[] {
  return [
    employee.facePhoto,
    employee.standPhoto,
    employee.passportScan,
    employee.medicalPhoto,
  ].filter((url): url is string => Boolean(url && String(url).trim()));
}

async function prefetchUrl(url: string): Promise<void> {
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    if (typeof img.decode === "function") {
      await img.decode();
      return;
    }
    if (img.complete && img.naturalWidth > 0) return;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  } catch {
    // Prefetch failure must not block PDF; DOM poll / error path handles it.
  }
}

async function prefetchEmployeePhotos(employee: Employee): Promise<void> {
  await Promise.all(employeePhotoUrls(employee).map(prefetchUrl));
}

function isImgReady(img: HTMLImageElement): boolean {
  if (!img.getAttribute("src") && !img.src) return true;
  if (img.complete && img.naturalWidth > 0) return true;
  // Failed loads: complete with naturalWidth 0 — treat as settled so we don't hang.
  if (img.complete && img.naturalWidth === 0) return true;
  return false;
}

async function decodeImg(img: HTMLImageElement): Promise<void> {
  if (typeof img.decode !== "function") return;
  try {
    await img.decode();
  } catch {
    // decode rejects on error; settled via complete/naturalWidth checks
  }
}

async function waitForDomImages(root: HTMLElement): Promise<void> {
  const deadline = Date.now() + TIMEOUT_MS;

  while (Date.now() < deadline) {
    const images = Array.from(root.querySelectorAll("img"));
    if (images.length === 0) {
      await waitForPaint();
      return;
    }

    await Promise.all(images.map(decodeImg));

    if (images.every(isImgReady)) {
      await waitForPaint();
      return;
    }

    await delay(POLL_MS);
  }

  await waitForPaint();
}

/**
 * Prefetch employee photo URLs, then poll DOM images until each has painted
 * pixels (or failed), then settle one paint frame. Times out so bulk never hangs.
 */
export async function waitForCvImagesReady(
  root: HTMLElement,
  employee: Employee,
): Promise<void> {
  await prefetchEmployeePhotos(employee);
  await waitForDomImages(root);
}
